import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { User } from '../../types';
import TraineeDetail from './TraineeDetail'; // Import the new detail component
import './Trainees.css';

interface Application {
    id: number;
    trainee: User;
    status: string;
    created_at: string;
}

interface TraineesProps {
    currentUser: User;
}

const Trainees: React.FC<TraineesProps> = ({ currentUser }) => {
    const { t } = useLanguage();
    const [trainees, setTrainees] = useState<User[]>([]);
    const [applications, setApplications] = useState<Application[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTrainee, setSelectedTrainee] = useState<User | null>(null);

    const refreshData = async () => {
        try {
            const [traineesRes, applicationsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/trainees`, {
                    credentials: 'include',
                    cache: 'no-cache'
                }),
                fetch(`${API_BASE_URL}/api/trainer/applications`, {
                    credentials: 'include',
                    cache: 'no-cache'
                })
            ]);

            if (traineesRes.ok) {
                const traineesData = await traineesRes.json();
                setTrainees(traineesData);
            } else {
                console.error('Failed to refresh trainees list');
            }

            if (applicationsRes.ok) {
                const applicationsData = await applicationsRes.json();
                setApplications(applicationsData.applications);
            } else {
                console.error('Failed to refresh applications list');
            }
        } catch (err) {
            console.error('An unexpected error occurred while refreshing data.');
        }
    };

    const fetchTraineesAndApplications = async () => {
        setLoading(true);
        try {
            await refreshData();
        } catch (err) {
            setError('An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleApplication = async (applicationId: number, action: 'accept' | 'reject') => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/trainer/applications/${applicationId}/${action}`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                fetchTraineesAndApplications(); // Refresh data
            } else {
                alert(`Failed to ${action} application.`);
            }
        } catch (error) {
            console.error(`Error ${action}ing application:`, error);
        }
    };

    const handleSelectTrainee = (trainee: User) => {
        setSelectedTrainee(trainee);
    };

    const handleBackToList = () => {
        setSelectedTrainee(null);
        fetchTraineesAndApplications(); // Refresh list to update unread count
    };

    useEffect(() => {
        // Initial fetch when component mounts
        fetchTraineesAndApplications();

        let intervalId: NodeJS.Timeout | null = null;

        // Start polling only when the main list is visible
        if (!selectedTrainee) {
            intervalId = setInterval(refreshData, 5000); // Poll every 5 seconds
        }

        // Cleanup function to clear interval when the component unmounts
        // or when a trainee is selected.
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [selectedTrainee]); // Re-run this effect when a trainee is selected or deselected

    if (loading) {
        return <div className="card">{t('common.loading')}</div>;
    }

    if (error) {
        return <div className="card" style={{ color: 'red' }}>{error}</div>;
    }

    if (selectedTrainee) {
        return (
            <TraineeDetail
                trainee={selectedTrainee}
                currentUser={currentUser}
                onBack={handleBackToList}
            />
        );
    }

    return (
        <div>
            <div className="card">
                <h2>Oczekujące Aplikacje</h2>
                {applications.length === 0 ? (
                    <p>Brak oczekujących aplikacji.</p>
                ) : (
                    <table className="user-table">
                        <thead>
                            <tr>
                                <th>Użytkownik</th>
                                <th>Data Aplikacji</th>
                                <th>Akcje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map((app) => (
                                <tr key={app.id}>
                                    <td>{app.trainee.first_name} {app.trainee.last_name}</td>
                                    <td>{new Date(app.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <button className="button-accept" onClick={() => handleApplication(app.id, 'accept')}>Akceptuj</button>
                                        <button className="button-reject" onClick={() => handleApplication(app.id, 'reject')}>Odrzuć</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <div className="card">
                <h2>Twoi Podopieczni</h2>
                {trainees.length === 0 ? (
                    <p>{t('trainees.noTrainees')}</p>
                ) : (
                    <div className="table-container">
                        <table className="user-table clickable-rows">
                            <thead>
                                <tr>
                                    <th>{t('userList.username')}</th>
                                    <th>{t('userList.firstName')}</th>
                                    <th>{t('userList.lastName')}</th>
                                    <th>{t('userList.club')}</th>
                                    <th>{t('userList.unreadMessages')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trainees.map((trainee) => (
                                    <tr key={trainee.id} onClick={() => handleSelectTrainee(trainee)}>
                                        <td>{trainee.username}</td>
                                        <td>{trainee.first_name}</td>
                                        <td>{trainee.last_name}</td>
                                        <td>{trainee.club ? t('common.yes') : t('common.no')}</td>
                                        <td>
                                            {trainee.unread_messages && trainee.unread_messages > 0 ? (
                                                <span className="unread-indicator">{trainee.unread_messages}</span>
                                            ) : (
                                                <span>0</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Trainees; 